import { type TriggerProps } from '@atlaskit/popup';
import { useCommentActionsContext } from '@databases/comments/hooks/useCommentContext';
import SelectValueMenu from '@databases/context-menu/select-value-menu';
import { createContextualMenuContext } from '@databases/context-menu/select-value-menu/utils/create-contextual-menu-context';
import {
  createContextualMenuItems,
  type MenuItemMap,
} from '@databases/context-menu/select-value-menu/utils/create-contextual-menu-items';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import { useDialog } from '@databases/dialog';
import FieldNameWithActions from '@databases/field-name/FieldNameWithActions';
import { useDatabaseSelectionSnapshot } from '@databases/ping-registry';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectIsValueSelected,
  selectSelectedEntireEntryIds,
} from '@databases/redux/cellSelections/selectors';
import { calculate2DValueRange } from '@databases/redux/cellSelections/utils';
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';
import { getService } from '@databases/service-container';
import { getDatabaseFieldRequired } from '@databases/utils/database/utils';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import DatabaseValueInlineEdit from '@databases/value/DatabaseValueInlineEdit';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  type MutableRefObject,
  useCallback,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { isNativeContextMenuTarget } from './native-context-menu-target';

export interface DatabaseEntryRowProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  isContentEditable: boolean;
  canEditContent: boolean;
  canEditFields: boolean;
  editingIds?: MutableRefObject<Set<string>>;
  autoFocus?: boolean;
  showDisabledValue?: boolean;
  emptyValueLabel?: string;
  selectedPanelFieldId: string | null;
  selectPanelField: (fieldId: string) => void;
  clearPanelSelection: () => void;
}

/**
 * Renders a table that shows all the field names and values of a database entry. The field names and values can be edited
 * using inline edit fields, and new fields can be added. Any edits will cause a database update to be drafted automatically.
 */
const DatabaseEntryRow: FunctionComponent<DatabaseEntryRowProps> = ({
  contentId,
  entryId,
  fieldId,
  isContentEditable,
  canEditContent,
  editingIds,
  autoFocus,
  canEditFields,
  showDisabledValue = false,
  emptyValueLabel,
  selectedPanelFieldId,
  selectPanelField,
  clearPanelSelection,
}) => {
  const store = defaultStoreUtils.useStore();
  const selectionsActions = getService('SelectionsActions');
  const { view } = useView();
  const [isHover, setIsHover] = useState(false);

  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );

  const [field, reduxIsSelected] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const entriesSelected = selectSelectedEntireEntryIds(state, {
      contentId,
    });
    const isSelected = entriesSelected.length
      ? undefined
      : selectIsValueSelected(state, { entryId, fieldId });
    return [getDatabaseFieldRequired(database, fieldId), isSelected] as const;
  });

  // Ignore row selections so the panel only opens editing from a selected cell.
  const pingIsOnlySelected = useDatabaseSelectionSnapshot(
    (reader) =>
      reader.getSelectionMode() === 'cell' &&
      reader.getSelectionCount() === 1 &&
      reader.isValueSelected(entryId, fieldId)
  );

  // Panel-local selection for fields hidden from the grid (no grid coord).
  const isPanelSelected = selectedPanelFieldId === fieldId;

  const isSelected = isSelectionsPerformanceEnabled
    ? pingIsOnlySelected || isPanelSelected
    : reduxIsSelected;

  const initialIsEditing = editingIds?.current.has(field.id);
  if (initialIsEditing) {
    // Since we use editingIds only to set the initialIsEditing prop, it is enough to set it once during the initial
    // render of the inline edit field. We don't want the field to steal the focus again when it is hidden and then
    // shown again because of a filter change. Because of this, we remove the entry from the list once it has been
    // focused for the first time.
    editingIds?.current.delete(field.id);
  }

  const { toggleOpenMenu } = useContextualMenu({
    type: 'value',
    entryId,
    fieldId,
    panel: true,
  });

  /**
   * Selection logic moved from Redux thunk `selectValueRange`.
   *
   * - Single click: Select the single value
   * - Shift+click: Select range from first selected value to clicked value
   */
  const onIsSelectedChange = useCallback(
    (options?: { isMultiSelectOn?: boolean }) => {
      if (!isContentEditable) {
        return;
      }

      if (options?.isMultiSelectOn) {
        const database = getDatabaseWithCurrentView(store.getState(), { contentId });

        if (!database) {
          selectionsActions.selectValues([{ entryId, fieldId }]);
          return;
        }

        const reader = getService('SelectionsReader');
        const currentSelectedEntryIds = reader.getEntryIdsFromSelection();
        const currentSelectedFieldIds = reader.getFieldIdsFromSelection();

        const values = calculate2DValueRange(
          database.entries,
          database.fields,
          currentSelectedEntryIds,
          currentSelectedFieldIds,
          entryId,
          fieldId
        );

        selectionsActions.selectValues(values);
      } else {
        selectionsActions.selectValues([{ entryId, fieldId }]);
      }
    },
    [isContentEditable, store, selectionsActions, contentId, entryId, fieldId]
  );

  const isContextMenuEnabled = isPluginEnabled(PluginKey.CONTEXT_MENU);

  const [menuItems, setMenuItems] = useState<MenuItemMap>({});
  const [canInsertContent, setCanInsertContent] = useState(false);
  const dialogs = useDialog();
  const { setOpenThread } = useCommentActionsContext();
  const { formatMessage } = useIntl();
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');

  const onCloseMenu = useCallback(() => {
    setMenuItems({});
  }, [setMenuItems]);

  const handleOnContextMenu = useCallback(
    (e: React.MouseEvent) => {
      onIsSelectedChange();

      if (isNativeContextMenuTarget(e.target)) {
        return;
      }

      if (isContentEditable) {
        e.preventDefault();
        toggleOpenMenu();

        const context = createContextualMenuContext({
          canEditContent,
          canEditFields,
          comments: {
            setOpenThread,
          },
          currentViewId: view.id,
          contentId,
          dialogs,
          entryId,
          fieldId,
          formatMessage,
          isDBAiFirstCreationEnabled,
        });
        const computedMenuItems = createContextualMenuItems(context);

        setMenuItems(computedMenuItems);
        setCanInsertContent(context.capabilities.canInsertContent);
      }
    },

    [
      onIsSelectedChange,
      isContentEditable,
      toggleOpenMenu,
      canEditContent,
      canEditFields,
      setOpenThread,
      contentId,
      dialogs,
      entryId,
      fieldId,
      formatMessage,
      isDBAiFirstCreationEnabled,
      view,
    ]
  );

  const triggerComponent = useCallback(
    (triggerProps: TriggerProps) => (
      <td
        onContextMenu={isContextMenuEnabled ? handleOnContextMenu : undefined}
        ref={triggerProps?.ref}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={
          isSelectionsPerformanceEnabled && canEditContent
            ? () => {
                // In selectionsPerformance mode, panel cell click/edit is driven
                // via SelectionsApi. First click selects; second click edits.
                // Hidden fields use panel-local state since they have no grid position.
                const api = getService('SelectionsApi');
                const isGridSelected =
                  api.getSelectionMode() === 'cell' &&
                  api.getSelectionCount() === 1 &&
                  api.isValueSelected(entryId, fieldId);

                if (isPanelSelected || isGridSelected) {
                  clearPanelSelection();
                  api.startEditing(entryId, fieldId, 'panel');
                  return;
                }

                clearPanelSelection();
                api.selectValues([{ entryId, fieldId }]);
                if (!api.isValueSelected(entryId, fieldId)) {
                  api.deselectAll();
                  selectPanelField(fieldId);
                }
              }
            : undefined
        }
      >
        <DatabaseValueInlineEdit
          contentId={contentId}
          fieldId={fieldId}
          entryId={entryId}
          initialIsEditing={initialIsEditing || autoFocus}
          isReadonly={!canEditContent}
          isContentEditable={isContentEditable}
          isSelected={isSelected}
          onIsSelectedChange={onIsSelectedChange}
          enableSelection
          panel={true}
          isHoverCell={isHover}
          setIsHoverCell={setIsHover}
          isRenderedInSidePanel
          showDisabledValue={showDisabledValue}
          emptyValueLabel={emptyValueLabel}
          {...triggerProps}
        />
      </td>
    ),
    [
      autoFocus,
      canEditContent,
      clearPanelSelection,
      contentId,
      entryId,
      fieldId,
      handleOnContextMenu,
      initialIsEditing,
      isContentEditable,
      isPanelSelected,
      isHover,
      isSelected,
      isSelectionsPerformanceEnabled,
      onIsSelectedChange,
      isContextMenuEnabled,
      selectPanelField,
      showDisabledValue,
      emptyValueLabel,
    ]
  );

  return (
    <tr
      onContextMenu={(e) => {
        if (!isNativeContextMenuTarget(e.target)) {
          e.preventDefault();
        }
      }}
    >
      <th id={fieldId}>
        <FieldNameWithActions
          contentId={contentId}
          fieldId={fieldId}
          isReadonly={!canEditFields}
          isSelected={false}
        />
      </th>
      <SelectValueMenu
        Trigger={triggerComponent}
        contentId={contentId}
        currentViewId={view.id}
        canEditContent={canEditContent}
        entryId={entryId}
        fieldId={fieldId}
        placement={'auto-end'}
        panel={true}
        menuItems={menuItems}
        canInsertContent={canInsertContent}
        onCloseMenu={onCloseMenu}
      />
    </tr>
  );
};

export default DatabaseEntryRow;
